import classNames from 'classnames';
import { type CSSProperties, useEffect, useRef, useState } from 'react';

import { getBackgroundColorStyle } from '../../utils/background';
import * as styles from '../Navbar.css';
import type { NavbarMenuBurgerContentElementProps } from '../Navbar.type';
import { useNavbarMenuBurgerContext } from './context';

const DEFAULTS = {
  background: '#ffffff',
  padding: '16px',
  gap: '4px',
};

export function NavbarMenuBurgerContent(props: NavbarMenuBurgerContentElementProps) {
  const { element, children, className } = props;
  const { customAttributes, background, padding, gap } = element.attrs || {};
  const contentRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState<number>(0);

  const { isOpen } = useNavbarMenuBurgerContext();

  // Observe content height changes
  useEffect(() => {
    if (!contentRef.current) return;

    const updateHeight = () => {
      if (contentRef.current) {
        const parent = contentRef.current.parentElement;
        const bar = parent?.previousElementSibling;
        if (bar) {
          setBarHeight(bar.clientHeight);
        }
      }
    };

    // Create observer to watch for height changes in children
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(contentRef.current);

    // Initial height calculation
    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    // biome-ignore lint/a11y/useValidAriaRole: none
    <div
      {...props}
      role="none"
      {...customAttributes}
      className={classNames(styles.navbarMenuBurgerContent)}
      style={
        {
          width: '100%',
          maxHeight: isOpen ? `min(${1000 - barHeight}px, calc(100vh - ${barHeight}px))` : '0px',
          height: isOpen ? 'auto' : '0px',
          overflow: isOpen ? 'auto' : 'hidden',
          opacity: isOpen ? 1 : 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'start',
          ...getBackgroundColorStyle(background ?? DEFAULTS.background),
        } as CSSProperties
      }
    >
      {/* biome-ignore lint/a11y/useValidAriaRole: none */}
      <div
        ref={contentRef}
        role="none"
        className={classNames(styles.navbarMenuBurgerContentInner, className, customAttributes?.className)}
        style={
          {
            width: '100%',
            '--padding': padding ?? DEFAULTS.padding,
            '--gap': gap ?? DEFAULTS.gap,
          } as CSSProperties
        }
      >
        {children}
      </div>
    </div>
  );
}
